import React, { useEffect, useState } from "react";
import {
  LayoutDashboard, Building2, Inbox, BadgeCheck, ArrowRight, Star,
  Clock4, TrendingUp, Users, Crown, AlertCircle, CheckCircle2, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import DashboardLayout from "../../components/DashboardLayout";
import { FORM_OPTIONS } from "../../lib/mockData";
import { auth, api } from "../../lib/api";

const LEAD_STATUS = {
  "New": "bg-[#EAF6D6] text-[#466B1F]",
  "Assigned": "bg-[#D5EAE6] text-[#0B5447]",
  "Quote sent": "bg-[#D5EAE6] text-[#0B5447]",
  "Scheduled": "bg-amber-100 text-amber-700",
  "Filing": "bg-[#CDEFD4] text-[#1B6B34]",
};
const DEFAULT_AVATAR = "https://images.pexels.com/photos/8112121/pexels-photo-8112121.jpeg?auto=compress&cs=tinysrgb&w=400";
const ALL_SPECS = ["AI/ML","Software","Semiconductor","SaaS","Biotech","Pharma","Chemistry","Mechanical","Electronics","Defence","Design"];

export default function AttorneyDashboard() {
  const [active, setActive] = useState("overview");
  const [profile, setProfile] = useState(auth.user);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const [p, l] = await Promise.all([api.attorneyProfile(), api.attorneyLeads()]);
      setProfile(p); auth.updateUser(p);
      setLeads(l);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { refresh(); }, []);

  const newLeads = leads.filter(l => l.status === "New" || l.status === "Assigned").length;
  const nav = [
    { id: "overview", label: "Overview", desc: "Your practice at a glance", icon: LayoutDashboard },
    { id: "leads", label: "Leads & Appointments", desc: "Innovator briefs assigned to you", icon: Inbox, badge: newLeads || undefined },
    { id: "profile", label: "Firm Profile", desc: "Manage how innovators see you", icon: Building2 },
    { id: "verification", label: "Verification", desc: "Approval status & history", icon: BadgeCheck },
  ];

  return (
    <DashboardLayout
      role="attorney" roleLabel="Attorney Dashboard" nav={nav} active={active} setActive={setActive}
      user={{ name: profile?.name || "Attorney", sub: profile?.firm_name || profile?.phone || "", avatar: profile?.avatar || DEFAULT_AVATAR }}
      logoutTo="/firm/login"
    >
      {loading ? (
        <div className="flex items-center justify-center h-96 text-[#5A6B66]"><Loader2 className="animate-spin mr-2"/> Loading…</div>
      ) : (
        <>
          {active === "overview" && <Overview profile={profile} leads={leads} setActive={setActive} />}
          {active === "leads" && <LeadsPanel leads={leads} />}
          {active === "profile" && <ProfilePanel profile={profile} onSave={async (data) => { await api.updateAttorneyProfile(data); toast.success("Submitted for admin approval"); await refresh(); }} />}
          {active === "verification" && <VerificationPanel profile={profile} />}
        </>
      )}
    </DashboardLayout>
  );
}

function Overview({ profile, leads, setActive }) {
  const newLeads = leads.filter(l => l.status === "New" || l.status === "Assigned").length;
  const stats = [
    { label: "Active leads", value: leads.length, icon: Inbox },
    { label: "Awaiting reply", value: newLeads, icon: Clock4 },
    { label: "Rating", value: profile?.rating || "—", icon: TrendingUp },
    { label: "Patents filed", value: (profile?.patents_filed || 0).toLocaleString(), icon: Users },
  ];
  const status = profile?.verification_status || "draft";
  return (
    <div className="space-y-6" data-testid="attorney-overview">
      <div className="rounded-2xl surface-deep text-white p-7 lg:p-8 flex items-center justify-between gap-6 flex-wrap">
        <div>
          <div className="chip chip-gold inline-flex mb-3"><Crown size={12}/> {profile?.tier || "Basic Listing"}</div>
          <h2 className="font-display font-extrabold text-3xl mb-1">{profile?.firm_name || "Complete your firm profile"}</h2>
          <p className="text-white/70 flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1"><Star size={14} className="fill-[#7CC242] text-[#7CC242]"/> {profile?.rating || "—"} ({profile?.reviews || 0})</span>
            <span>·</span><span>{profile?.patents_filed || 0} patents filed</span>
            <span>·</span>
            <span className={`inline-flex items-center gap-1 ${status === "approved" ? "text-[#7CC242]" : "text-amber-300"}`}>
              <BadgeCheck size={14}/> {status === "approved" ? "Verified & Live" : status === "pending" ? "Pending approval" : "Draft — complete profile"}
            </span>
          </p>
        </div>
        <button onClick={() => setActive("leads")} className="btn-accent"><Inbox size={16}/> View leads</button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="card p-6">
            <div className="w-11 h-11 rounded-xl surface-sage flex items-center justify-center text-[#0E6E5E] mb-4"><s.icon size={20}/></div>
            <div className="font-display font-extrabold text-4xl text-[#0A2520]">{s.value}</div>
            <div className="text-sm text-[#5A6B66] mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card p-6">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-display font-bold text-xl text-[#0A2520]">Latest leads</h3>
          <button onClick={() => setActive("leads")} className="text-sm font-semibold text-[#0E6E5E] inline-flex items-center gap-1">View all <ArrowRight size={14}/></button>
        </div>
        <div className="space-y-3">
          {leads.slice(0, 4).map((l) => (
            <div key={l.id} className="flex items-center gap-4 p-4 rounded-xl surface-sage">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[#0A2520] truncate">{l.patent_title}</div>
                <div className="text-xs text-[#5A6B66]">{l.customer_name} · {l.entity} · {l.city}</div>
              </div>
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap ${LEAD_STATUS[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status}</span>
            </div>
          ))}
          {leads.length === 0 && <div className="text-sm text-[#5A6B66] p-3">No leads yet. Admin will assign customer briefs to you once your profile is approved.</div>}
        </div>
      </div>
    </div>
  );
}

function LeadsPanel({ leads }) {
  return (
    <div className="card p-0 overflow-hidden" data-testid="attorney-leads">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[820px]">
          <thead>
            <tr className="bg-[#0A2520] text-white text-left">
              {["Innovator", "Patent brief", "City", "Received", "Appointment", "Status"].map((h) => (
                <th key={h} className="p-4 font-display font-semibold whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {leads.map((l, i) => (
              <tr key={l.id} className={`border-t border-[#F0F4F0] ${i % 2 ? "bg-[#FAFCFA]" : "bg-white"}`} data-testid={`lead-row-${l.id}`}>
                <td className="p-4"><div className="font-semibold text-[#0A2520]">{l.customer_name}</div><div className="text-xs text-[#5A6B66]">{l.entity}</div></td>
                <td className="p-4"><div className="text-[#0A2520] max-w-[220px] truncate">{l.patent_title}</div><div className="text-xs text-[#5A6B66]">{l.type} · ₹{(l.value || 0).toLocaleString('en-IN')}</div></td>
                <td className="p-4 text-[#3D4F4A] whitespace-nowrap">{l.city}</td>
                <td className="p-4 text-[#5A6B66] whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-4 text-[#3D4F4A] whitespace-nowrap">{l.appt || "—"}</td>
                <td className="p-4"><span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${LEAD_STATUS[l.status] || "bg-slate-100 text-slate-700"}`}>{l.status}</span></td>
              </tr>
            ))}
            {leads.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-[#5A6B66]">No leads assigned to you yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProfilePanel({ profile, onSave }) {
  const [form, setForm] = useState({
    firm_name: profile?.firm_name || "",
    name: profile?.name || "",
    designation: profile?.designation || "Managing Partner",
    email: profile?.email || "",
    city: profile?.city || profile?.location || "Bengaluru",
    tagline: profile?.tagline || "",
    bio: profile?.bio || "",
    price_from: profile?.price_from || 15000,
    attorneys: profile?.attorneys || 1,
    established: profile?.established || 2020,
    specializations: profile?.specializations || [],
    languages: profile?.languages || ["English"],
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const toggleSpec = (s) => set("specializations", form.specializations.includes(s) ? form.specializations.filter(x => x !== s) : [...form.specializations, s]);

  const save = async () => {
    if (!form.firm_name) { toast.error("Firm name is required"); return; }
    setBusy(true);
    try { await onSave(form); } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="attorney-profile">
      <div className="card p-5 flex items-start gap-3 bg-amber-50 border-amber-200">
        <AlertCircle size={20} className="text-amber-600 flex-shrink-0 mt-0.5"/>
        <div>
          <div className="font-display font-semibold text-amber-800">Changes require admin approval</div>
          <p className="text-sm text-amber-700">Any edits are submitted to the Udhyak admin team. Your live profile updates only after approval.</p>
        </div>
      </div>

      <div className="card p-7">
        <div className="grid sm:grid-cols-2 gap-5">
          <div><Label>Firm name *</Label><input className="input" value={form.firm_name} onChange={(e) => set("firm_name", e.target.value)} data-testid="firm-name"/></div>
          <div><Label>Primary contact</Label><input className="input" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="firm-contact"/></div>
          <div><Label>Designation</Label><input className="input" value={form.designation} onChange={(e) => set("designation", e.target.value)}/></div>
          <div><Label>City</Label><select className="input" value={form.city} onChange={(e) => set("city", e.target.value)}>{FORM_OPTIONS.cities.map(c => <option key={c}>{c}</option>)}</select></div>
          <div><Label>Email</Label><input className="input" value={form.email} onChange={(e) => set("email", e.target.value)}/></div>
          <div><Label>Tagline</Label><input className="input" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} placeholder="Boutique tech-IP practice"/></div>
          <div><Label>Starting price (₹)</Label><input type="number" className="input" value={form.price_from} onChange={(e) => set("price_from", Number(e.target.value))}/></div>
          <div><Label>No. of attorneys</Label><input type="number" className="input" value={form.attorneys} onChange={(e) => set("attorneys", Number(e.target.value))}/></div>
        </div>

        <div className="mt-5">
          <Label>Specialisations</Label>
          <div className="flex flex-wrap gap-2">
            {ALL_SPECS.map((s) => (
              <button key={s} onClick={() => toggleSpec(s)} type="button" className={`px-3 py-1.5 rounded-full text-sm font-semibold ${form.specializations.includes(s) ? "bg-[#0E6E5E] text-white" : "bg-[#EBF3EC] text-[#0B5447]"}`}>{s}</button>
            ))}
          </div>
        </div>

        <div className="mt-5">
          <Label>Firm bio</Label>
          <textarea rows={4} className="input resize-none leading-relaxed" value={form.bio} onChange={(e) => set("bio", e.target.value)} placeholder="Describe your firm's focus, experience, and specialties."/>
        </div>

        <div className="flex gap-3 mt-8 pt-6 border-t border-[#F0F4F0]">
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-50" data-testid="attorney-submit-changes">
            {busy ? <Loader2 size={16} className="animate-spin"/> : "Submit changes for approval"}
          </button>
        </div>
      </div>
    </div>
  );
}

function VerificationPanel({ profile }) {
  const status = profile?.verification_status || "draft";
  const isLive = status === "approved";
  const isPending = status === "pending";
  return (
    <div className="max-w-3xl space-y-6" data-testid="attorney-verification">
      <div className="card p-7">
        <div className="flex items-center gap-4 mb-2">
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${isLive ? "bg-[#CDEFD4] text-[#1B6B34]" : isPending ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
            {isLive ? <CheckCircle2 size={28}/> : <Clock4 size={28}/>}
          </div>
          <div>
            <div className="chip chip-accent inline-flex mb-1"><BadgeCheck size={12}/> {status}</div>
            <h2 className="font-display font-bold text-2xl text-[#0A2520]">
              {isLive ? "Your profile is live & verified" : isPending ? "Awaiting admin approval" : "Complete your profile to get listed"}
            </h2>
          </div>
        </div>
        <p className="text-[#5A6B66] mt-3">
          {isLive
            ? "Your firm is fully verified and visible to all innovators on Udhyak. Any future edits will be re-reviewed before publishing."
            : isPending
              ? "Our admin team is reviewing your submission. This typically takes under 24 hours."
              : "Fill out your firm profile and submit for verification to start receiving leads."}
        </p>
      </div>
    </div>
  );
}

function Label({ children }) {
  return <label className="block font-display text-sm font-semibold text-[#0A2520] mb-2">{children}</label>;
}
